'use client'

import React, { Component, ReactNode } from 'react'
import axios from 'axios'
import { ClientError } from '@/lib/interfaces/misc'
import Image from 'next/image'

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Función para detectar si el user agent es un bot
const isBotUserAgent = (): boolean => {
  if (typeof navigator === 'undefined') return false

  const userAgent = navigator.userAgent.toLowerCase()
  const botPatterns = [
    'googlebot',
    'bingbot',
    'slurp',
    'duckduckbot',
    'baiduspider',
    'yandexbot',
    'facebookexternalhit',
    'twitterbot',
    'linkedinbot',
    'whatsapp',
    'telegrambot',
    'applebot',
    'ia_archiver',
    'crawler',
    'spider',
    'bot',
    'headlesschrome',
    'phantom',
    'scrapy',
    'curl',
    'wget',
    'python-requests',
    'lighthouse',
    'pagespeed'
  ]

  return botPatterns.some(pattern => userAgent.includes(pattern))
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static MAX_RELOAD_ATTEMPTS = 3

  constructor (props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError (_: Error): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch (error: Error, errorInfo: React.ErrorInfo) {
    // Detectar si es un error de carga de chunk
    if (error.message.includes('Loading chunk')) {
      const reloadAttempts = parseInt(sessionStorage.getItem('reloadAttempts') || '0', 10)

      if (reloadAttempts < ErrorBoundary.MAX_RELOAD_ATTEMPTS) {
        sessionStorage.setItem('reloadAttempts', (reloadAttempts + 1).toString())
        window.location.reload()
      } else {
        console.error('Máximo de intentos de recarga alcanzado. No se recargará la página.')

        // Solo reportar errores si no es un bot
        if (!isBotUserAgent()) {
          const errorData: ClientError = {
            message: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
            userAgent: navigator.userAgent,
            url: window.location.href
          }

          // Reportar el error al backend después de exceder los intentos de recarga
          axios.post('/api/error-client', errorData)
            .catch(err => {
              console.error('Fallo al reportar el error:', err)
            })
        }
      }
    } else {
      // Para otros tipos de errores, reportarlos inmediatamente solo si no es un bot
      if (!isBotUserAgent()) {
        const errorData: ClientError = {
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          userAgent: navigator.userAgent,
          url: window.location.href
        }

        // Reportar errores al backend
        axios.post('/api/error-client', errorData)
          .catch(err => {
            console.error('Fallo al reportar el error:', err)
          })
      }
    }
  }

  handleReload = () => {
    // Reiniciar contador de intentos de recarga
    sessionStorage.setItem('reloadAttempts', '0')
    window.location.reload()
  }

  render () {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <Image
              src='https://res.cloudinary.com/dt0hfjec6/image/upload/v1718433493/bm-logo_kkluxy.png'
              alt='Barmanager Logo'
              width={200}
              height={100}
              style={styles.logo}
            />
            <hr style={styles.separator} />
            <h1 style={styles.title}>Algo salió mal</h1>
            <p style={styles.message}>Lamentamos el inconveniente. Por favor, intenta recargar la página.</p>
            <button style={styles.button} onClick={this.handleReload}>
              Recargar página
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
    height: '100vh',
    backgroundColor: 'var(--color-gray-25)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
    boxSizing: 'border-box'
  },
  content: {
    width: '90%',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '40px 20px',
    boxShadow: '0 4px 12px color-mix(in srgb, var(--color-black) 10%, transparent)',
    textAlign: 'center',
    boxSizing: 'border-box'
  },
  logo: {
    width: '60%',
    maxWidth: '200px',
    marginBottom: '20px',
    height: 'auto'
  },
  separator: {
    width: '80%',
    margin: '20px auto',
    border: '0',
    borderBottom: '1px solid var(--color-overlay-faint-2)'
  },
  title: {
    color: 'var(--color-gray-700)',
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '10px'
  },
  message: {
    fontSize: '16px',
    color: 'var(--color-gray-750)',
    lineHeight: '1.6',
    marginBottom: '30px'
  },
  button: {
    padding: '10px 20px',
    fontSize: '16px',
    color: '#ffffff',
    backgroundColor: 'var(--color-success-bright-3)',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease'
  }
}

export default ErrorBoundary
